/**
 * PrintPage – Main application page for Document Generation & Printing.
 * Enterprise shell: compact app bar, breadcrumb trail, full-width workspace.
 * Help menu: quick Joyride tour + full docs.
 */

import * as React from "react";
import { DocxMarkerWizard } from "@/components/DocxMarkerWizard/DocxMarkerWizard";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RecentJobsHeader } from "@/components/RecentJobsPanel";
import { LanguageSwitcher, useTranslation } from "@/i18n";
import { Printer, ChevronRight, Building2, FileText, HelpCircle, PlayCircle, BookOpen } from "lucide-react";
import { resetOnboarding, type TourPhase } from "@/utils/onboardingStore";
import { cn } from "@/lib/utils";

interface PrintPageProps {
  /** Open the Help page. */
  onOpenHelp?: () => void;
}

const ENV_BADGE: { label: string; tone: string } | null = (() => {
  const env =
    (typeof import.meta !== "undefined" &&
      (import.meta as { env?: Record<string, string> }).env?.MODE) ||
    "";
  if (env === "production") return null;
  if (env === "staging")
    return { label: "STAGING", tone: "bg-amber-500/15 text-amber-800 border-amber-500/40" };
  if (env === "development" || env === "dev")
    return { label: "Enterprise Edition", tone: "bg-sky-500/15 text-sky-800 border-sky-500/40" };
  return env
    ? { label: env.toUpperCase(), tone: "bg-slate-500/10 text-slate-600 border-slate-500/30" }
    : null;
})();

export default function PrintPage({ onOpenHelp }: PrintPageProps) {
  const { t } = useTranslation();
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [tourReplayNonce, setTourReplayNonce] = React.useState(0);
  const [tourForcePhase, setTourForcePhase] = React.useState<TourPhase | "all" | undefined>();
  const helpRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!helpOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!helpRef.current?.contains(e.target as Node)) setHelpOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [helpOpen]);

  function startQuickTour() {
    resetOnboarding(); // allow full replay
    setTourForcePhase("all");
    setTourReplayNonce((n) => n + 1);
    setHelpOpen(false);
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[hsl(210_20%_98%)] text-foreground" data-tour="tour-app-header">
        {/* Top brand strip */}
        <div className="h-0.5 w-full bg-primary" aria-hidden />

        <header className="sticky top-0 z-50 w-full border-b border-border/80 bg-background/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
          <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between gap-4 px-3 md:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm ring-1 ring-black/5">
                <Printer className="h-[18px] w-[18px]" />
              </div>
              <div className="min-w-0 flex flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-[15px] font-semibold tracking-tight leading-none">
                    In Ấn Chứng Từ
                  </h1>
                  <span className="shrink-0 rounded border border-primary/20 bg-primary/5 px-1.5 py-px text-[10px] font-bold tracking-wide text-primary">
                    BizBox.Asia
                  </span>
                  {ENV_BADGE && (
                    <span
                      className={`shrink-0 rounded border px-1.5 py-px text-[10px] font-bold tracking-wide ${ENV_BADGE.tone}`}
                    >
                      {ENV_BADGE.label}
                    </span>
                  )}
                </div>
                <nav
                  className="flex items-center gap-1 text-[11px] text-muted-foreground"
                  aria-label="Breadcrumb"
                >
                  <Building2 className="h-3 w-3 shrink-0 opacity-70" />
                  <span className="font-medium text-foreground/70">BIZBOX TOOLS</span>
                  <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
                  <span className="truncate">{t("app.enterpriseEdition")}</span>
                  <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
                  <span className="truncate text-foreground/80">In chứng từ</span>
                </nav>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <div className="relative" ref={helpRef} data-tour="tour-help">
                <button
                  type="button"
                  onClick={() => setHelpOpen((v) => !v)}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-md border border-border/70 bg-background/60 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
                    helpOpen && "bg-muted text-foreground"
                  )}
                  title={t("tour.menuTitle")}
                  aria-label={t("tour.menuTitle")}
                  aria-expanded={helpOpen}
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
                {helpOpen && (
                  <div className="absolute right-0 z-[60] mt-1 w-56 overflow-hidden rounded-md border bg-popover py-1 shadow-md">
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("tour.menuTitle")}
                    </p>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted"
                      onClick={startQuickTour}
                    >
                      <PlayCircle className="h-3.5 w-3.5 text-primary" />
                      {t("tour.replay")}
                    </button>
                    {onOpenHelp && (
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted"
                        onClick={() => {
                          setHelpOpen(false);
                          onOpenHelp();
                        }}
                      >
                        <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                        {t("tour.openDocs")}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <RecentJobsHeader />
              <LanguageSwitcher />
            </div>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1600px] px-3 py-3 md:px-6 md:py-4">
          {/* Page intro */}
          <div className="mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <p className="text-xs text-muted-foreground">
              Tạo và in các chứng từ doanh nghiệp từ dữ liệu Excel — phiếu giao hàng, hoá đơn, báo giá, phiếu kho, phiếu lương.
            </p>
          </div>

          <DocxMarkerWizard
            tourReplayNonce={tourReplayNonce}
            tourForcePhase={tourForcePhase}
          />
        </main>

        <footer className="border-t border-border/60 py-3">
          <div className="mx-auto flex w-full max-w-[1600px] flex-col items-center justify-between gap-1 px-3 text-center text-[11px] text-muted-foreground md:flex-row md:px-6">
            <p>© {new Date().getFullYear()} BizBox ERP. Bảo lưu mọi quyền.</p>
            <p className="flex items-center gap-1">
              <Building2 className="h-3 w-3 opacity-60" />
              BizBox.Asia — Giải pháp quản trị doanh nghiệp
            </p>
          </div>
        </footer>
      </div>
    </TooltipProvider>
  );
}
